<template>
  <div class="parent-onboarding-section">
    <!-- AVATAR -->
    <div class="w-100 d-flex justify-content-center">
      <ChildIcon></ChildIcon>
    </div>
    <!-- AVATAR -->

    <!-- ONBOARDING TITLE -->
    <div class="onboarding-title font-weight-bold brand_navy text-center">
      Add your Child
    </div>

    <!-- ONBOARDING ALERT CARD -->
    <div class="alert-onboarding-card mx-auto text-center rounded-5">
      Student already registered?
      <router-link
        :to="{ name: 'ParentConnectChildOnboarding' }"
        class="connect-link link-underline"
        >Connect with student code</router-link
      >
    </div>

    <!-- FORM AREA -->
    <form action @submit.prevent="registerChild" autocomplete="off">
      <!-- CHILD NAME -->
      <div class="form-group">
        <label for="childName" class="control-label color_grey_dark"
          >Child's Name</label
        >
        <input
          type="text"
          v-model="form.child_fullname"
          id="childName"
          class="form-control"
          required
          placeholder="Enter Child Name (First and Last name only)"
        />
      </div>

      <!-- RELATIONSHIP -->
      <div class="form-group">
        <label for="relationship" class="control-label color_grey_dark"
          >Relationship</label
        >
        <select
          class="form-control"
          id="relationship"
          required
          v-model="form.relationship"
        >
          <option value selected disabled>
            - How are you related to the child? -
          </option>
          <option value="father">Father</option>
          <option value="mother">Mother</option>
          <option value="guardian">Guardian</option>
        </select>
      </div>

      <!-- CLASS -->
      <div class="form-group">
        <label for="class" class="control-label color_grey_dark">Class</label>
        <select class="form-control" id="class" required v-model="form.class">
          <option value="" selected disabled>
            - What class is your child? -
          </option>
          <option
            :value="class_name.value"
            v-for="(class_name, index) in class_list"
            :key="index"
          >
            {{ class_name.title }}
          </option>
        </select>
      </div>

      <!-- PASSWORD -->
      <div class="form-group">
        <label for="password" class="control-label color_grey_dark"
          >Create Password</label
        >
        <div class="input-group">
          <input
            :type="passwordType ? 'password' : 'text'"
            v-model="form.password"
            class="form-control"
            id="password"
            required
            minlength="6"
            placeholder="Create a login password for your child"
          />
          <span class="input-group-addon show-pwd" @click="showPwd">
            <i class="pointer cust_icon icon_eye"></i>
          </span>
        </div>
      </div>

      <!-- POST SCRIPT -->
      <div class="extra-text border_grey_dark">
        <span class="brand_tonic font-weight-bold">P.S:</span> A student code
        will be generated for your child. This along with the password you
        entered above will be required of your child to login
      </div>

      <!-- LINE SEPARATOR -->
      <div class="form-divider w-100"></div>

      <!-- SUBMIT BUTTON -->
      <div class="w-100 d-flex justify-content-end align-items-start mb-5">
        <button
          class="btn btn-md btn-accent font-weight-bold brand_navy"
          :disabled="isDisabled"
          ref="addChildBtn"
        >
          ADD CHILD
        </button>
      </div>
    </form>

    <!-- MODALS -->
    <ChildAddedModal
      v-if="child_added"
      :student="student_data"
      @toFeed="feedPage"
      @closeTriggered="toggleNewChildModal"
    ></ChildAddedModal>

    <ResponseDialog
      v-if="response_dialog"
      :alert_msg="alert_card_msg"
      :alert_type="alert_card_type"
      @closeAlert="toggleResponseDialog"
    ></ResponseDialog>
    <!-- MODALS -->
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ChildIcon from "@/components/coloredIconPack/ChildIcon";

export default {
  name: "NewChildForm",

  components: {
    ChildIcon,
    ChildAddedModal: () =>
      import(
        /* webpackChunkName: "ChildAddedModal" */ "@/components/modalComps/parentModals/ChildAddedModal"
      ),
    ResponseDialog: () =>
      import(
        /* webpackChunkName: "ResponseDialog" */ "@/components/globalComps/ResponseDialog"
      ),
  },

  data() {
    return {
      form: {
        child_fullname: "",
        relationship: "",
        class: "",
        password: "",
      },

      passwordType: true,
      child_added: false,
      class_list: [],

      response_dialog: false,
      alert_card_msg: "",
      alert_card_type: "",
    };
  },

  computed: {
    isDisabled() {
      if (
        this.form.child_fullname.split(" ").length !== 2 &&
        this.form.relationship === "" &&
        this.form.class === "" &&
        (this.form.password === "" || this.form.password.length <= 3)
      )
        return true;
      else return false;
    },
  },

  mounted() {
    this.fetchClassList();
  },

  methods: {
    ...mapActions([
      "getGlobalClassList",
      "addNewChild",
      "updateBoardingStatus",
    ]),

    feedPage() {
      this.$emit("toFeedPage");
    },

    toggleResponseDialog() {
      this.response_dialog = !this.response_dialog;
    },

    popUpAlert(msg) {
      this.alert_card_msg = msg;
      this.alert_card_type = "error";
      this.toggleResponseDialog();
    },

    toggleNewChildModal() {
      this.child_added = !this.child_added;
    },

    fetchClassList() {
      this.getGlobalClassList()
        .then((response) => {
          if (response.code === 200) {
            this.class_list = response.data.map((class_item) => {
              return {
                value: class_item.id,
                title: class_item.description,
              };
            });
          }
        })
        .catch((err) => console.log(err));
    },

    showPwd() {
      let pwd_icon = document.querySelector(".show-pwd");
      this.passwordType = !this.passwordType;
      pwd_icon.classList.toggle("show_pass");
    },

    validateStudent(child_data) {
      if (!child_data.first_name) {
        this.popUpAlert("Please enter student name");
        return false;
      } else if (!child_data.last_name) {
        this.popUpAlert("Please enter student last name");
        return false;
      } else if (!child_data.class) {
        this.popUpAlert("Please select student class");
        return false;
      } else if (!child_data.relationship) {
        this.popUpAlert("Please select your relationship with student");
        return false;
      } else if (!child_data.password) {
        this.popUpAlert("Please enter student password");
        return false;
      } else return true;
    },

    registerChild() {
      this.$refs.addChildBtn.disabled = false;
      this.$refs.addChildBtn.innerHTML =
        '<div class="icon-dotted-roller font-17 mr-2 animate"></div>Adding ...';

      // SETUP NEW OBJECT
      let child_data = {
        first_name: this.form.child_fullname.split(" ")[0],
        last_name: this.form.child_fullname.split(" ")[1],
        class: this.form.class,
        relationship: this.form.relationship,
        password: this.form.password,
      };

      // VALIDATE ENTRIES
      if (!this.validateStudent(child_data)) {
        this.$refs.addChildBtn.disabled = false;
        this.$refs.addChildBtn.innerHTML = "Add Child";
        return false;
      } else {
        this.addNewChild(child_data)
          .then((response) => {
            this.$refs.addChildBtn.disabled = false;
            this.$refs.addChildBtn.innerText = "Add Child";
            if (response.code === 200) {
              this.updateBoardingStatus();
              this.student_data = response.data;

              this.$refs.addChildBtn.disabled = false;
              this.$refs.addChildBtn.innerText = "Add Child";

              this.toggleNewChildModal();

              this.form.child_fullname = "";
              this.form.relationship = "";
              this.form.class = "";
              this.form.password = "";
            } else {
              this.popUpAlert(response.message);
            }
          })
          .catch((err) => console.log(err));
      }
    },
  },
};
</script>

<style lang="scss">
.parent-onboarding-section {
  .extra-text {
    font-size: 12.5px;
    line-height: 18px;
    margin-top: 10px;
  }

  .form-divider {
    margin: 45px auto 30px;
  }
}
</style>
